import Layout from "@/components/Layout";
import { Link } from "wouter";
import Breadcrumb from "@/components/Breadcrumb";

const PPC_CANONICAL = "https://localrankingcoach.com/services/ppc-management";

const ppcSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": PPC_CANONICAL,
    url: PPC_CANONICAL,
    name: "PPC Management Services for Contractors & Local Businesses | Local Ranking Coach",
    description: "PPC management services for contractors and local businesses \u2014 Google Ads, Local Services Ads, call-only campaigns, and paid search management that generates qualified leads.",
    breadcrumb: {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: "https://localrankingcoach.com" },
        { "@type": "ListItem", position: 2, name: "Services", item: "https://localrankingcoach.com/services" },
        { "@type": "ListItem", position: 3, name: "PPC Management", item: PPC_CANONICAL },
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question", name: "What is PPC advertising and how much does it cost?", acceptedAnswer: { "@type": "Answer", text: "Pay-per-click advertising is an online advertising model where advertisers pay a fee each time someone clicks their ad. Costs depend on campaign complexity, keyword competition, industry, and monthly ad budget. Management fees are separate from your ad spend and vary by agency and scope." } },
      { "@type": "Question", name: "How quickly can PPC generate leads for my business?", acceptedAnswer: { "@type": "Answer", text: "PPC campaigns can generate clicks and leads within hours of launch. However, stronger performance typically develops over the first 30\u201390 days as data accumulates, campaigns are optimized, and bidding strategies are refined based on real conversion data." } },
      { "@type": "Question", name: "Why hire a PPC management company instead of doing it yourself?", acceptedAnswer: { "@type": "Answer", text: "Professional PPC managers bring platform expertise, advanced optimization tools, negative keyword management, audience segmentation, and ongoing testing that most business owners lack the time to implement. Poorly managed campaigns waste significant budget on non-converting clicks." } },
      { "@type": "Question", name: "What platforms does Local Ranking Coach manage PPC campaigns on?", acceptedAnswer: { "@type": "Answer", text: "We manage campaigns across Google Search Ads, Google Display Ads, YouTube Ads, Google Local Service Ads, Bing Ads, Facebook Ads, Instagram Ads, and LinkedIn Ads \u2014 creating a cross-platform paid media strategy aligned with your business goals." } },
      { "@type": "Question", name: "Do I retain ownership of my Google Ads account?", acceptedAnswer: { "@type": "Answer", text: "Yes. Local Ranking Coach provides full transparent account access. You retain ownership of your Google Ads account and all campaign data. We operate as a trusted manager, never a gatekeeper." } },
      { "@type": "Question", name: "How does PPC management work with SEO?", acceptedAnswer: { "@type": "Answer", text: "PPC and SEO are complementary strategies. PPC delivers immediate visibility and leads while SEO builds long-term organic rankings. When combined, they create full-funnel coverage \u2014 capturing both immediate buyers and research-phase prospects across all stages of the customer journey." } },
    ],
  },
];

export default function PPCManagementPage() {

  const LeadForm = ({ city = "" }: { city?: string }) => (
    <form
      className="bg-white rounded-xl shadow-lg p-6 space-y-4"
      onSubmit={(e) => e.preventDefault()}
    >
      <h3 className="text-lg font-bold text-[#1a2e4a]">Get Your Free PPC Audit</h3>
      <p className="text-sm text-gray-500">Discover wasted ad spend and quick wins — no obligation.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]" placeholder="Full Name *" required />
        <input type="email" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]" placeholder="Email Address *" required />
        <input type="tel" className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]" placeholder="Phone Number" />
        <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]" placeholder="Monthly Ad Budget" defaultValue={city} />
      </div>
      <select className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]">
        <option value="">Select Campaign Type...</option>
        <option>Google Search Ads</option>
        <option>Google Local Service Ads</option>
        <option>Bing Ads</option>
        <option>Facebook / Instagram Ads</option>
        <option>YouTube Ads</option>
        <option>Full Cross-Platform PPC</option>
      </select>
      <textarea className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#c9a84c]" rows={3} placeholder="Tell us about your business and advertising goals..." />
      <button type="submit" className="w-full bg-[#c9a84c] hover:bg-[#b8963e] text-white font-bold py-3 rounded-lg transition-all duration-150 active:scale-[0.97]">
        → Get My Free PPC Audit
      </button>
      <p className="text-xs text-center text-gray-400">No spam. No obligation. 100% confidential.</p>
    </form>
  );

  return (
    <Layout
      title="PPC Management Services for Small Businesses | Local Ranking Coach"
      description="PPC management services for contractors and local businesses — Google Ads, Local Services Ads, call-only campaigns, and paid search management that generates qualified leads."
      canonical={PPC_CANONICAL}
      schema={ppcSchema}
    >
      <Breadcrumb items={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/services" },
          { label: "PPC Management" },
        ]}
      />
      {/* Hero */}
      <section className="bg-[#1a2e4a] text-white py-20 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="flex flex-wrap gap-2 mb-4">
              <span className="bg-[#c9a84c]/20 text-[#c9a84c] text-xs font-semibold px-3 py-1 rounded-full border border-[#c9a84c]/30">Google Ads Certified</span>
              <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/20">Veteran-Owned Agency</span>
              <span className="bg-white/10 text-white text-xs font-semibold px-3 py-1 rounded-full border border-white/20">Trusted by 500+ Businesses</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold leading-tight mb-4">
              Best <span className="text-[#c9a84c]">Pay Per Click Management Agency</span> in America for High-Converting PPC Campaigns
            </h1>
            <p className="text-lg text-gray-300 mb-6">
              Pay-per-click advertising delivers immediate visibility and qualified leads when managed with strategic planning, continuous optimization, and deep platform expertise. Local Ranking Coach is a digital marketing agency specializing in <strong className="text-white">PPC services</strong> and paid media strategies that generate high-quality leads at a cost that makes sense.
            </p>
            <div className="flex flex-wrap gap-3 mb-8">
              {["Google Ads Management", "Local Service Ads", "Bing Ads", "Landing Page Optimization", "Conversion Tracking", "Transparent Reporting"].map((s) => (
                <span key={s} className="bg-white/10 text-white text-xs px-3 py-1 rounded-full border border-white/20">{s}</span>
              ))}
            </div>
            <div className="flex flex-wrap gap-4">
              <a href="tel:18003016220" className="bg-[#c9a84c] hover:bg-[#b8963e] text-white font-bold px-6 py-3 rounded-lg transition-all duration-150 active:scale-[0.97]">Get Free PPC Audit →</a>
              <a href="tel:18003016220" className="border border-white/30 text-white hover:bg-white/10 font-semibold px-6 py-3 rounded-lg transition-all duration-150">📞 1-800-301-6220</a>
            </div>
          </div>
          <div>
            <LeadForm />
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-[#c9a84c] py-6 px-4">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 text-center text-white">
          {[
            { stat: "24–48 hrs", label: "Campaign Launch Time" },
            { stat: "8 Platforms", label: "Paid Media Channels" },
            { stat: "3× ROI", label: "Average Return on Ad Spend" },
            { stat: "100%", label: "Transparent Account Access" },
          ].map((item) => (
            <div key={item.label}>
              <div className="text-2xl font-extrabold">{item.stat}</div>
              <div className="text-sm font-medium opacity-90">{item.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* What Is PPC + Why It Matters */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-extrabold text-[#1a2e4a] mb-4">What Is Pay Per Click Management?</h2>
            <p className="text-gray-600 mb-4">
              <strong>Pay-per-click advertising</strong> is an online advertising model where advertisers pay a fee each time someone clicks their ad. Unlike organic SEO, PPC delivers immediate visibility at the top of search results — making it the fastest way to generate qualified leads for your business.
            </p>
            <p className="text-gray-600 mb-6">
              Professional <strong>PPC management</strong> ensures your budget is allocated to the right keywords, audiences, and platforms — eliminating wasted spend on broad keywords, weak landing pages, and clicks that never convert.
            </p>
            <h3 className="text-xl font-bold text-[#1a2e4a] mb-3">Why Businesses Need PPC Management Services</h3>
            <ul className="space-y-2">
              {[
                "Generate qualified leads faster than SEO alone",
                "Appear at the top of Google search results immediately",
                "Control exactly how much you spend per day and per click",
                "Target specific locations, devices, audiences, and times of day",
                "Measure every dollar spent with conversion tracking and ROI reporting",
              ].map((item) => (
                <li key={item} className="flex items-start gap-2 text-gray-700 text-sm">
                  <span className="text-[#c9a84c] mt-0.5">✓</span>{item}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#1a2e4a] mb-4">Types of PPC Campaigns We Manage</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { name: "Google Search Ads", icon: "🔍", desc: "High-intent keyword targeting" },
                { name: "Google Display Ads", icon: "🖼️", desc: "Visual brand awareness campaigns" },
                { name: "YouTube Ads", icon: "▶️", desc: "Video advertising at scale" },
                { name: "Bing Ads", icon: "🅱️", desc: "Microsoft Advertising platform" },
                { name: "Local Service Ads", icon: "📍", desc: "Google Guaranteed lead ads" },
                { name: "Shopping Ads", icon: "🛒", desc: "E-commerce product campaigns" },
                { name: "Retargeting Campaigns", icon: "🔄", desc: "Re-engage past site visitors" },
                { name: "Social Media Ads", icon: "📱", desc: "Facebook, Instagram, LinkedIn" },
              ].map((item) => (
                <div key={item.name} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <div className="text-xl mb-1">{item.icon}</div>
                  <div className="font-semibold text-[#1a2e4a] text-sm">{item.name}</div>
                  <div className="text-xs text-gray-500">{item.desc}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why LRC PPC Works */}
      {/* 3-Step Process */}
      <section className="py-16 px-4 bg-[#1a2e4a] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold mb-3">How Our PPC Campaign Strategy Works</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">A structured, data-driven process that eliminates wasted ad spend and maximizes return on every dollar invested in paid advertising.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: "01",
                title: "Strategic Campaign Planning",
                desc: "We research your market, competitors, target audience, keywords, and budget before launching a single campaign.",
                items: ["Keyword research & intent mapping", "Negative keyword planning", "Competitor ad analysis", "Budget allocation strategy", "Bidding strategy development", "Audience segmentation setup"] },
              {
                step: "02",
                title: "Campaign Launch & Optimization",
                desc: "We create ads, optimize landing pages, configure targeting, and launch campaigns engineered for performance from day one.",
                items: ["Ad copy creation & A/B testing", "Landing page optimization", "Conversion tracking setup", "Geo-targeting configuration", "Device & schedule bid adjustments", "Quality Score improvement"] },
              {
                step: "03",
                title: "Ongoing Management & Growth",
                desc: "We monitor performance, refine bids, test ad copy, scale winning campaigns, and systematically reduce wasted ad spend.",
                items: ["Weekly performance monitoring", "Bid management & adjustments", "Ad copy testing & refresh", "Audience expansion & exclusions", "Monthly reporting & strategy calls", "Campaign scaling for growth"] },
            ].map((item) => (
              <div key={item.step} className="bg-white/10 rounded-xl p-6 border border-white/20">
                <div className="text-[#c9a84c] text-4xl font-extrabold mb-2">{item.step}</div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-300 text-sm mb-4">{item.desc}</p>
                <ul className="space-y-1">
                  {item.items.map((i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-300 text-xs">
                      <span className="text-[#c9a84c] mt-0.5">✓</span>{i}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What Makes LRC Different */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-extrabold text-[#1a2e4a] mb-4">What Makes Local Ranking Coach Different</h2>
            <p className="text-gray-600 mb-6">Most PPC agencies focus on clicks. We focus on conversions, qualified leads, and measurable revenue growth — the metrics that actually matter to your business.</p>
            <div className="space-y-4">
              {[
                { title: "Advanced Campaign Optimization", desc: "We use testing frameworks, audience segmentation, conversion optimization, and data-driven campaign adjustments — not set-it-and-forget-it management." },
                { title: "Industry Specialization", desc: "We understand the differences between PPC campaigns for home services, healthcare, legal services, e-commerce, and local businesses — and build campaigns accordingly." },
                { title: "Landing Page + Web Development Support", desc: "A high-performing PPC campaign needs fast, mobile-friendly, conversion-focused landing pages. We build and optimize landing pages that turn clicks into leads." },
                { title: "SEO + PPC Integration", desc: "SEO builds long-term organic visibility while PPC generates immediate traffic. We align both strategies for full-funnel digital marketing coverage." },
              ].map((item) => (
                <div key={item.title} className="flex gap-4">
                  <div className="w-8 h-8 bg-[#c9a84c] rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 mt-0.5">✓</div>
                  <div>
                    <h3 className="font-bold text-[#1a2e4a] mb-1">{item.title}</h3>
                    <p className="text-gray-600 text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#1a2e4a] mb-4">Key PPC Performance Metrics We Track</h3>
            <div className="grid grid-cols-2 gap-3">
              {[
                { metric: "CTR", label: "Click-Through Rate", icon: "👆" },
                { metric: "CPC", label: "Cost Per Click", icon: "💰" },
                { metric: "CPL", label: "Cost Per Lead", icon: "📋" },
                { metric: "ROAS", label: "Return on Ad Spend", icon: "📈" },
                { metric: "CVR", label: "Conversion Rate", icon: "🎯" },
                { metric: "QS", label: "Quality Score", icon: "⭐" },
                { metric: "Impressions", label: "Ad Visibility", icon: "👁️" },
                { metric: "Revenue", label: "Attributed Revenue", icon: "💵" },
              ].map((item) => (
                <div key={item.metric} className="bg-[#1a2e4a] text-white rounded-lg p-3 text-center">
                  <div className="text-xl mb-1">{item.icon}</div>
                  <div className="font-extrabold text-[#c9a84c] text-lg">{item.metric}</div>
                  <div className="text-xs text-gray-300">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-[#1a2e4a] mb-3">Industries That Benefit from Our PPC Advertising</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">We build industry-specific PPC campaigns that understand the unique search behavior, seasonality, and conversion patterns of each market.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
            {[
              { industry: "HVAC Companies", icon: "❄️", desc: "HVAC PPC campaigns capture seasonal demand and ready-to-buy customers searching for heating and cooling services.", keywords: ["AC repair near me", "HVAC installation cost", "Emergency furnace repair"] },
              { industry: "Roofing Contractors", icon: "🏠", desc: "Roofing PPC targets high-value service searches from homeowners actively looking for roof repairs or replacement.", keywords: ["Roof repair near me", "Roofing contractor cost", "Emergency roof leak"] },
              { industry: "Electricians", icon: "⚡", desc: "Electrician PPC captures emergency searches and high-intent electrical service leads from local homeowners.", keywords: ["Emergency electrician", "Electrical panel upgrade", "Licensed electrician near me"] },
              { industry: "Plumbing Companies", icon: "🔧", desc: "Plumbing PPC helps companies compete in crowded local markets with geo-targeted, high-intent campaigns.", keywords: ["Emergency plumber near me", "Drain cleaning service", "Water heater replacement"] },
              { industry: "Medical Practices", icon: "🏥", desc: "Medical PPC requires compliance awareness, specialty targeting, and patient-focused messaging for healthcare providers.", keywords: ["Primary care doctor near me", "Urgent care open now", "Specialist appointment"] },
              { industry: "Attorneys & Law Firms", icon: "⚖️", desc: "Legal PPC uses advanced keyword strategies and conversion-focused landing pages to generate qualified consultations.", keywords: ["Personal injury attorney", "DUI lawyer near me", "Free legal consultation"] },
              { industry: "E-Commerce Stores", icon: "🛒", desc: "E-commerce PPC uses shopping ads, display ads, retargeting, and search campaigns to drive online sales and revenue.", keywords: ["Buy [product] online", "Best price [product]", "Free shipping [product]"] },
              { industry: "Local Service Businesses", icon: "📍", desc: "Local service businesses benefit from affordable campaigns focused on calls, leads, and booked jobs in their service area.", keywords: ["[Service] near me", "[Service] in [City]", "Best [service] company"] },
            ].map((item) => (
              <div key={item.industry} className="bg-white rounded-xl border border-gray-200 p-5 shadow-sm hover:shadow-md transition-shadow">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="font-bold text-[#1a2e4a] text-base mb-2">{item.industry}</h3>
                <p className="text-gray-600 text-xs mb-3 leading-relaxed">{item.desc}</p>
                <div className="space-y-1">
                  {item.keywords.map((kw) => (
                    <span key={kw} className="block text-xs bg-[#c9a84c]/10 text-[#8a6a1e] px-2 py-0.5 rounded font-medium">{kw}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Landing Pages + Social + Cross-Platform */}
      <section className="py-16 px-4 bg-[#1a2e4a] text-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold mb-3">A Complete Paid Media Strategy — Not Just Google Ads</h2>
            <p className="text-gray-300 max-w-2xl mx-auto">Combining search ads, display ads, retargeting, social ads, and optimized landing pages creates stronger campaign coverage and better ROI across every channel.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: "Landing Page Optimization",
                icon: "🖥️",
                desc: "A high-performing PPC campaign needs fast, mobile-friendly, conversion-focused landing pages. Strong landing page design improves user experience, conversion rates, and return on ad spend.",
                items: ["Mobile-first responsive design", "Fast page load speed (Core Web Vitals)", "Clear call-to-action above the fold", "Trust signals and social proof", "A/B testing for continuous improvement"] },
              {
                title: "Paid Social Media Advertising",
                icon: "📱",
                desc: "Paid social media advertising helps reach targeted audiences across Facebook, Instagram, LinkedIn, and other platforms — expanding reach beyond search intent.",
                items: ["Facebook & Instagram Ads", "LinkedIn B2B advertising", "Lookalike audience targeting", "Retargeting past site visitors", "Cross-platform attribution tracking"] },
              {
                title: "SEO + Content + PPC Integration",
                icon: "🔗",
                desc: "SEO builds long-term organic visibility while PPC generates immediate traffic. Strong content supports landing pages, improves trust, and helps convert paid traffic into customers.",
                items: ["Keyword data sharing between SEO & PPC", "Content-supported landing pages", "Organic + paid SERP domination", "Unified reporting dashboard", "Full-funnel attribution modeling"] },
            ].map((item) => (
              <div key={item.title} className="bg-white/10 rounded-xl p-6 border border-white/20">
                <div className="text-3xl mb-3">{item.icon}</div>
                <h3 className="text-xl font-bold mb-2">{item.title}</h3>
                <p className="text-gray-300 text-sm mb-4">{item.desc}</p>
                <ul className="space-y-1">
                  {item.items.map((i) => (
                    <li key={i} className="flex items-start gap-2 text-gray-300 text-xs">
                      <span className="text-[#c9a84c] mt-0.5">✓</span>{i}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold text-[#1a2e4a] mb-3">Google Ads Management Services & Pricing</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Transparent PPC management packages designed for local businesses, growing companies, and enterprise advertisers — with no hidden fees and full account access.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                tier: "Local PPC Starter",
                price: "Starting at $497/mo",
                badge: null,
                desc: "Perfect for local service businesses ready to generate more calls and leads from Google Ads.",
                features: ["Google Local Service Ads setup", "Geo-targeted search campaigns", "Service-area keyword targeting", "Budget-friendly campaign planning", "Monthly performance reporting", "Transparent account access"],
                cta: "Get Started" },
              {
                tier: "Growth Campaign",
                price: "Starting at $997/mo",
                badge: "Most Popular",
                desc: "Ideal for growing businesses ready to scale leads across Google Search, Display, and retargeting.",
                features: ["Everything in Local Starter", "Google Search + Display Ads", "Retargeting campaign setup", "Landing page optimization", "A/B ad copy testing", "Bi-weekly strategy calls"],
                cta: "Get Started" },
              {
                tier: "Enterprise PPC",
                price: "Custom Pricing",
                badge: null,
                desc: "Full-service paid media management for multi-location businesses and enterprise advertisers.",
                features: ["Everything in Growth Campaign", "Multi-platform campaign management", "YouTube & social media ads", "Custom reporting dashboards", "Dedicated account manager", "Advanced conversion tracking"],
                cta: "Contact Us" },
            ].map((item) => (
              <div key={item.tier} className={`rounded-xl border-2 p-6 relative ${item.badge ? "border-[#c9a84c] shadow-lg" : "border-gray-200"}`}>
                {item.badge && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#c9a84c] text-white text-xs font-bold px-4 py-1 rounded-full">{item.badge}</div>
                )}
                <h3 className="text-xl font-bold text-[#1a2e4a] mb-1">{item.tier}</h3>
                <div className="text-[#c9a84c] font-extrabold text-lg mb-2">{item.price}</div>
                <p className="text-gray-600 text-sm mb-4">{item.desc}</p>
                <ul className="space-y-2 mb-6">
                  {item.features.map((f) => (
                    <li key={f} className="flex items-start gap-2 text-gray-700 text-sm">
                      <span className="text-[#c9a84c] mt-0.5">✓</span>{f}
                    </li>
                  ))}
                </ul>
                <a href="tel:18003016220" className={`block text-center font-bold py-3 rounded-lg transition-all duration-150 active:scale-[0.97] ${item.badge ? "bg-[#c9a84c] hover:bg-[#b8963e] text-white" : "border-2 border-[#1a2e4a] text-[#1a2e4a] hover:bg-[#1a2e4a] hover:text-white"}`}>
                  {item.cta}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Common Mistakes */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <h2 className="text-3xl font-extrabold text-[#1a2e4a] mb-4">Common PPC Mistakes That Waste Your Budget</h2>
            <p className="text-gray-600 mb-6">Poorly managed PPC campaigns waste money on broad keywords, weak landing pages, and clicks that do not convert. These are the most costly mistakes we fix for new clients.</p>
            <div className="space-y-3">
              {[
                { mistake: "Broad match keywords without negatives", impact: "Paying for irrelevant clicks that never convert" },
                { mistake: "Sending traffic to the homepage", impact: "Low conversion rates from unfocused landing pages" },
                { mistake: "No conversion tracking setup", impact: "Unable to measure ROI or optimize campaigns" },
                { mistake: "Ignoring Quality Score", impact: "Higher CPCs and lower ad positions" },
                { mistake: "No A/B testing on ad copy", impact: "Stagnant performance with no improvement path" },
                { mistake: "Setting and forgetting campaigns", impact: "Budget drain without ongoing optimization" },
              ].map((item) => (
                <div key={item.mistake} className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="font-semibold text-red-800 text-sm">✗ {item.mistake}</div>
                  <div className="text-red-600 text-xs mt-1">{item.impact}</div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <h2 className="text-3xl font-extrabold text-[#1a2e4a] mb-4">Proof That Our PPC Management Delivers Results</h2>
            <p className="text-gray-600 mb-6">We monitor the metrics that matter — not just clicks and impressions, but qualified leads, cost per conversion, and measurable revenue growth.</p>
            <div className="space-y-4">
              {[
                { title: "E-Commerce Performance", icon: "🛒", desc: "Strong PPC management improves revenue, shopping ad performance, and return on ad spend for e-commerce brands — with Shopping Ads and retargeting working together to recover abandoned carts." },
                { title: "Local Service Lead Generation", icon: "📍", desc: "Local service campaigns generate qualified leads through Google Ads, Local Service Ads, and geo-targeted campaigns — delivering calls and form submissions from ready-to-buy customers." },
                { title: "Measurable Performance Improvements", icon: "📈", desc: "We monitor click-through rate, cost per click, cost per lead, return on ad spend, and conversion quality — providing monthly reports that show exactly where every dollar goes." },
              ].map((item) => (
                <div key={item.title} className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm">
                  <div className="text-2xl mb-2">{item.icon}</div>
                  <h3 className="font-bold text-[#1a2e4a] mb-1">{item.title}</h3>
                  <p className="text-gray-600 text-sm">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-extrabold text-[#1a2e4a] text-center mb-10">Frequently Asked Questions About PPC Management</h2>
          <div className="space-y-4">
            {[
              { q: "What is PPC advertising and how much does it cost?", a: "Pay-per-click advertising is an online advertising model where advertisers pay a fee each time someone clicks their ad. Costs depend on campaign complexity, keyword competition, industry, and monthly ad budget. Management fees are separate from your ad spend and vary by agency and scope." },
              { q: "How quickly can PPC generate leads for my business?", a: "PPC campaigns can generate clicks and leads within hours of launch. However, stronger performance typically develops over the first 30–90 days as data accumulates, campaigns are optimized, and bidding strategies are refined based on real conversion data." },
              { q: "Why hire a PPC management company instead of doing it yourself?", a: "Professional PPC managers bring platform expertise, advanced optimization tools, negative keyword management, audience segmentation, and ongoing testing that most business owners lack the time to implement. Poorly managed campaigns waste significant budget on non-converting clicks." },
              { q: "What platforms does Local Ranking Coach manage PPC campaigns on?", a: "We manage campaigns across Google Search Ads, Google Display Ads, YouTube Ads, Google Local Service Ads, Bing Ads, Facebook Ads, Instagram Ads, and LinkedIn Ads — creating a cross-platform paid media strategy aligned with your business goals." },
              { q: "Do I retain ownership of my Google Ads account?", a: "Yes. Local Ranking Coach provides full transparent account access. You retain ownership of your Google Ads account and all campaign data. We operate as a trusted manager, never a gatekeeper." },
              { q: "How does PPC management work with SEO?", a: "PPC and SEO are complementary strategies. PPC delivers immediate visibility and leads while SEO builds long-term organic rankings. When combined, they create full-funnel coverage — capturing both immediate buyers and research-phase prospects across all stages of the customer journey." },
            ].map((item, idx) => (
              <details key={idx} className="bg-gray-50 border border-gray-200 rounded-xl p-5 group">
                <summary className="font-bold text-[#1a2e4a] cursor-pointer list-none flex justify-between items-center">
                  {item.q}
                  <span className="text-[#c9a84c] ml-4 flex-shrink-0 group-open:rotate-45 transition-transform duration-200">+</span>
                </summary>
                <p className="text-gray-600 text-sm mt-3 leading-relaxed">{item.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-2xl font-extrabold text-[#1a2e4a] text-center mb-8">Related Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { title: "Local SEO Services", href: "/services/local-seo", icon: "📍" },
              { title: "Google Business Profile", href: "/services/google-business-profile", icon: "📌" },
              { title: "AEO Agency", href: "/services/aeo-agency", icon: "🤖" },
              { title: "On-Page SEO", href: "/services/on-page-seo", icon: "📄" },
            ].map((s) => (
              <Link key={s.title} href={s.href} className="bg-white border border-gray-200 rounded-xl p-4 text-center hover:border-[#c9a84c] hover:shadow-md transition-all">
                <div className="text-2xl mb-2">{s.icon}</div>
                <div className="font-semibold text-[#1a2e4a] text-sm">{s.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="py-16 px-4 bg-[#1a2e4a] text-white">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl font-extrabold mb-4">Start Generating More Leads with Professional PPC Management</h2>
            <p className="text-gray-300 mb-6">Local Ranking Coach can analyze your current campaigns and identify immediate opportunities for improvement. Get a free PPC audit and speak with a PPC specialist about your business goals.</p>
            <div className="space-y-3">
              {[
                "Free PPC campaign audit with no obligation",
                "Google Ads certified campaign managers",
                "Transparent account access — you own your data",
                "Results-focused reporting on leads and ROI",
                "Veteran-owned agency with 20+ years of experience",
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <span className="w-5 h-5 bg-[#c9a84c] rounded-full flex items-center justify-center text-white text-xs flex-shrink-0">✓</span>
                  <span className="text-gray-300 text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <LeadForm />
          </div>
        </div>
      </section>

      {/* Related Industries */}
      <section className="py-16 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl font-extrabold text-navy mb-2 text-center">Industries We Serve</h2>
          <p className="text-gray-500 text-sm text-center mb-8">Specialized SEO for local service businesses across every major industry vertical.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
            {[
              { label: "Electricians", href: "/industries/electricians", icon: "⚡" },
              { label: "HVAC", href: "/industries/hvac-seo", icon: "❄️" },
              { label: "Plumbers", href: "/industries/plumbing", icon: "🔧" },
              { label: "Roofers", href: "/industries/roofing", icon: "🏠" },
              { label: "Healthcare", href: "/industries/healthcare-seo", icon: "🏥" },
              { label: "Legal", href: "/industries/legal-seo", icon: "⚖️" },
              { label: "Restaurants", href: "/industries/restaurants-seo", icon: "🍽️" },
              { label: "Home Services", href: "/industries/home-services", icon: "🛠️" },
              { label: "Automotive", href: "/industries/automotive-seo", icon: "🚗" },
              { label: "General Contractors", href: "/industries/general-contractors", icon: "🏗️" },
            ].map((ind) => (
              <a
                key={ind.href}
                href={ind.href}
                className="flex flex-col items-center gap-1.5 bg-white border border-gray-200 hover:border-gold/40 hover:shadow-sm rounded-xl p-3 text-center transition-all group"
              >
                <span className="text-xl">{ind.icon}</span>
                <span className="text-xs font-semibold text-navy group-hover:text-gold transition-colors">{ind.label}</span>
              </a>
            ))}
          </div>
        </div>
      </section>
          <PopularLocations />
</Layout>

  );
}
